/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { EzApi } from '/public/common/javascript/ezapi.js'
        import { EzUI } from '/public/common/javascript/ezui.js'
        import { ezUI } from '/public/common/javascript/ezui.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following helper classes into this class to avoid
    circular dependencies:
        import { EzObject } from '/ezlibrary/helpers/EzObject.js';
        import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
        import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
        import { EzFloat } from '/ezlibrary/helpers/EzFloat.js';
        import { EzChar } from '/ezlibrary/helpers/EzChar.js';
        import { EzString } from '/ezlibrary/helpers/EzString.js';
        import { EzStringConstants } from '/ezlibrary/helpers/EzStringConstants.js';
        import { EzArray } from '/ezlibrary/helpers/EzArray.js';
        import { EzMap } from '/ezlibrary/helpers/EzMap.js';
        import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
        import { EzError } from '/ezlibrary/helpers/EzError.js';
        import {
            DAY_IN_MONTH,
            EzDays
        } from '/ezlibrary/helpers/EzDays.js';
        import { EzJson } from '/ezlibrary/helpers/EzJson.js';
        import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
        import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
        import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
        import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';
import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * Provides commonly used regular expression constants.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other helper classes (that allow it) with:
 *     import { EzRegEx } from '/ezlibrary/helpers/EzRegEx.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-helper classes with:
 *  import {
 *       EzRegEx
 *       //, ... other helper classes
 *  } from '/ezlibrary/helpers/EzHelpers.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access via static fiels, properties, and methods only:
 *     EzRegEx.{field, property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzRegEx extends EzStaticClass {
    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match two or more sequential spaces.
     * @returns {object}
     * Returns regular expression: /( {2})+/g
     */
    static get MATCH_2_OR_MORE_SPACES() {
        return /( {2})+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /( {2})+/g
     * Used to match two or more spaces
     * @returns {RegExp}
     */
    static get regExp_MATCH_2_OR_MORE_SPACES() {
        return new RegExp(/( {2})+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all tab characters
     * @returns {object}
     * Returns regular expression: /\t+/g
     */
    static get MATCH_ALL_TABS() {
        return /\t+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /\t+/g
     * Used to match all tab characters
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_TABS() {
        return new RegExp(/\t+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all line feed characters
     * @returns {object}
     * Returns regular expression: /\n+/g
     */
    static get MATCH_ALL_LINEFEEDS() {
        return /\n+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /\n+/g
     * Used to match all line feed characters
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_LINEFEEDS() {
        return new RegExp(/\n+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all charrage returns and tabs feed characters
     * @returns {object}
     * Returns regular expression: /[\r\t]+/g
     */
    static get MATCH_ALL_RETURNS_AND_TABS() {
        return /[\r\t]+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /[\r\t]+/g
     * Used to match all charrage returns and tabs feed characters
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_RETURNS_AND_TABS() {
        return new RegExp(/[\r\t]+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all charrage returns and tabs feed characters
     * @returns {object}
     * Returns regular expression: /[\r\t]+/g
     */
    static get MATCH_ALL_TAB_LINEFEED_RETURN() {
        return /[\t\n\r]+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /[\t\n\r]+/g
     * Used to match all charrage returns and tabs feed characters
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_TAB_LINEFEED_RETURN() {
        return new RegExp(/[\t\n\r]+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all 'supported' control characters
     * Supported controller characters:
     * 1) Horizontal Tabulator: \t
     * 2) New Line: \n
     * 3) Carriage Return: \r
     * 4) Form Feed: \f
     * 5) Vertical Tabulator: \v
     * 6) Backspace: \b
     * @returns {object}
     * Returns regular expression: /[\b\t\n\r\f\v]+/g
     */
    static get MATCH_CONTROL_CHARACTERS() {
        return /[\b\t\n\r\f\v]+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /[\b\t\n\r\f\v]+/g
     * Used to match all 'supported' control characters
     * @returns {RegExp}
     */
    static get regExp_MATCH_CONTROL_CHARACTERS() {
        return new RegExp(/[\b\t\n\r\f\v]+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the regular expression to match all white space characters
     * White space characters include all control characters and the space character.
     * @returns {object}
     * Returns regular expression: /\s+/g
     */
    static get MATCH_ALL_WHITESPACE_CHARACTERS() {
        return /\s+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /\s+/g
     * Used to match all white space characters
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_WHITESPACE_CHARACTERS() {
        return new RegExp(/\s+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the regular expression to match only the following characters:
     * 1) Space: ' '
     * 2) Horizontal Tabulator: '\t'
     * 3) New Line: '\n'
     * 4) Carriage Return: '\r'
     * @returns {object}
     * Returns regular expression: /[ \t\n\r]+/g
     */
    static get MATCH_SPACE_N_R_T() {
        return /[ \t\n\r]+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /[ \t\n\r]+/g
     * Used to match only the following characters:
     * @returns {RegExp}
     */
    static get regExp_MATCH_SPACE_N_R_T() {
        return new RegExp(/[ \t\n\r]+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all:
     * 1) Two or more sequential spaces: '  '
     * 2) Horizontal Tabulator: '\t'
     * 3) New Line: '\n'
     * 4) Carriage Return: '\r'
     * @returns {object}
     * Returns regular expression: /( {2})+[\t\n\r]+/g
     */
    static get MATCH_ALL_2SPACES_TAB_LINEFEED_RETURN() {
        return /( {2})+[\t\n\r]+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /( {2})+[\t\n\r]+/g
     * Used to match all sequences of two or more spaces followed by tab, line feed, or carriage return characters
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_2SPACES_TAB_LINEFEED_RETURN() {
        return new RegExp(/( {2})+[\t\n\r]+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all the following characters:
     * 1) Bang: '!'
     * 2) Tick: '\''
     * 3) Open Parenthesis: '('
     * 4) Close Parenthesis: ')'
     * 5) Astrix: '*'
     * Example:
     *  "WOW! 'that' was amazing (I think)".replaceAll(EzRegEx.MATCH_ALL_2SPACES_TAB_LINEFEED_RETURN, '');
     *  Output: 'WOW that was amazing I think'
     * @returns {object}
     * Returns regular expression: /[!'()*]/g
     */
    static get MATCH_ALL_BANG_TICK_PARENTHESIS_ASTRIX() {
        return /[!'()*]/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /[!'()*]/g
     * Used to match all the following characters:
     * 1) Bang: '!'
     * 2) Tick: '\''
     * 3) Open Parenthesis: '('
     * 4) Close Parenthesis: ')'
     * 5) Astrix: '*'
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_BANG_TICK_PARENTHESIS_ASTRIX() {
        return new RegExp(/[!'()*]/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all ampersand ('&') characters.)
     * @returns {object}
     * Returns regular expression: /&/g
     */
    static get MATCH_AMPERSAND() {
        return /&/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /&/g
     * Used to match all ampersand ('&') characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_AMPERSAND() {
        return new RegExp(/&/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all double quote ('"') characters.)
     * @returns {object}
     * Returns regular expression: /"/g
     */
    static get MATCH_ALL_DOUBLE_QUOTE() {
        return /"/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /"/g
     * Used to match all double quote ('"') characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_DOUBLE_QUOTE() {
        return new RegExp(/"/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all back-slash ('\') characters
     * @returns {object}
     * Returns regular expression: /\//
     */
    static get MATCH_ALL_BACK_SLASH() {
        return /\//g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /\//g
     * Used to match all back-slash ('\') characters
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_BACK_SLASH() {
        return new RegExp(/\\/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all ampersand ('<') characters.)
     * @returns {object}
     * Returns regular expression: /</g
     */
    static get MATCH_ALL_LESS_THAN() {
        return /</g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /</g
     * Used to match all less than ('<') characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_LESS_THAN() {
        return new RegExp(/</, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all ampersand ('>') characters.)
     * @returns {object}
     * Returns regular expression: />/g
     */
    static get MATCH_ALL_GREATER_THAN() {
        return />/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: />/g
     * Used to match all greater than ('>') characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_GREATER_THAN() {
        return new RegExp(/>/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all ampersand ('\'') characters.)
     * @returns {object}
     * Returns regular expression: /'/g
     */
    static get MATCH_ALL_SINGLE_QUOTE() {
        return /'/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /'/g
     * Used to match all single quote ('\'') characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_SINGLE_QUOTE() {
        return new RegExp(/'/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all ampersand ('@') characters.)
     * @returns {object}
     * Returns regular expression: /@/g
     */
    static get MATCH_ALL_AT_SYMBOL() {
        return /@/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /@/g
     * Used to match all at symbol ('@') characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_AT_SYMBOL() {
        return new RegExp(/@/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all ampersand ('!') characters.)
     * @returns {object}
     * Returns regular expression: /!/g
     */
    static get MATCH_ALL_BANG() {
        return /!/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /!/g
     * Used to match all bang ('!') characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_BANG() {
        return new RegExp(/!/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a regular expression to match all ampersand ('=') characters.)
     * @returns {object}
     * Returns regular expression: /=/g
     */
    static get MATCH_ALL_EQUAL() {
        return /=/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /=/g
     * Used to match all equal ('=') characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_EQUAL() {
        return new RegExp(/=/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets regular expression constant: /-/g
     * Used to match all dash characters.
     * @returns {object}
     */
    static get MATCH_ALL_DASH() {
        return /-/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /-/g
     * Used to match all dash characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_DASH() {
        return new RegExp(/-/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets regular expression constant: /`/g
     * Used to match all back-tick characters.
     * @returns {/-/g}
     */
    static get MATCH_ALL_BACK_TICK() {
        return /`/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a new RegExp instance for regular expression: \`\g
     * Used to match all back-tick characters.
     * @returns {RegExp}
     */
    static get regExp_MATCH_ALL_BACK_TICK() {
        return new RegExp(/`/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets regular expression constant: /[\s_-]+/g
     * Used to match all sequences of one or more spaces, underscores, or dashes.
     * @returns {object}
     */
    static get MATCH_SPACE_UNDERSCORE_DASH() {
        return /[\s_-]+/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /[\s_-]+/g
     * Used to match all sequences of one or more spaces, underscores, or dashes.
     * @returns {RegExp}
     */
    static get regExp_MATCH_SPACE_UNDERSCORE_DASH() {
        return new RegExp(/[\s_-]+/, 'g');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets regular expression constant: /(?<=[a-z])(?=[A-Z])/g
     * Used to match the position between a lowercase letter and an uppercase letter, often used to split camelCase or PascalCase strings into separate words.
     * @returns {object}
     */
    static get MATCH_POSITION_BETWEEN_LOWERCASE_CHAR_AND_UPPERCASE_CHAR() {
        return /(?<=[a-z])(?=[A-Z])/g;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a RegExp instance for regular expression: /(?<=[a-z])(?=[A-Z])/g
     * Used to match the position between a lowercase letter and an uppercase letter, often used to split camelCase or PascalCase strings into separate words.     *
     * @returns {RegExp}
     */
    static get regExp_MATCH_POSITION_BETWEEN_LOWERCASE_CHAR_AND_UPPERCASE_CHAR() {
        return new RegExp(/(?<=[a-z])(?=[A-Z])/, 'g');
    }

    /**
     * @static
     * @public @function
     * Gets a RegExp instance for regular expression: new RegExp(`(${sortedWordList.join('|')})`, 'gi')
     * Used to match any word in the provided word list, regardless of case.
     * @param {array} wordList
     * A none-empty array of strings representing the words to be matched.
     * @returns {RegExp}
     */
    static regExp_WORD_LIST_MATHCER(...wordList) {
        if (!wordList || 0 >= (wordList?.length || 0) || wordList.some(word => 'string' !== typeof word)) {
            throw new EzBadParamException(
                { wordList },
                EzRegEx,
                EzRegEx.WORD_LIST_MATHCER);
        }

        let wordListSet = new Set(wordList);

        const sortedWordList = [...wordListSet].sort(
            (stringA, stringB) => stringB.length - stringA.length);

        return new RegExp(`(${sortedWordList.join('|')})`, 'gi');
    }

}
